import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, LogIn, Leaf, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Logo } from './Logo';

const Connexion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await login(formData);
      
      toast({
        title: "Connexion réussie !",
        description: "Bienvenue",
      });
      
      // Rediriger vers le compte
      const from = location.state?.from;
      if (Number(result.user?.is_admin) === 1) {
        navigate('/admin', { replace: true });
      } else if (from === 'pricing') {
        navigate('/mon-compte', { state: location.state });
      } else {
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erreur",
        description: "Email ou mot de passe incorrect",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-12 px-4 flex items-center">
      <div className="max-w-md mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center mb-4 hover:opacity-80 transition-opacity"
            data-testid="connexion-logo-home"
          >
            <img src="/logo-ler.png" alt="Location Benne Chantier" className="h-16 w-auto object-contain" />
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Connexion
          </h1>
          <p className="text-gray-600">
            Accédez à votre espace client
          </p>
        </div>

        <Card className="p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-base font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                className="mt-2 h-12"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="votre@email.fr"
                required
              />
            </div>

            {/* Mot de passe */}
            <div>
              <Label htmlFor="password" className="text-base font-semibold">Mot de passe</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="h-12 pr-12"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Votre mot de passe"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Bouton de soumission */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Connexion...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-5 w-5" />
                  Se connecter
                </>
              )}
            </Button>

            {/* Mot de passe oublié */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/mot-de-passe-oublie')}
                className="text-sm text-green-600 hover:text-green-700 font-medium hover:underline"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </button>
            </div>

            {/* Lien vers inscription */}
            <p className="text-center text-gray-600 mt-4">
              Pas encore de compte ?{' '}
              <button
                type="button"
                onClick={() => navigate('/inscription')}
                className="text-green-600 hover:text-green-700 font-semibold"
              >
                Créer un compte
              </button>
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Connexion;
